import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppointmentService from '../services/appointment/appointment-service';
import { Appointment, PatientAppointment } from '../services/appointment/types';

type AppointmentData = PatientAppointment & { moreData?: Appointment };

export const useVisitsService = (patientId?: number, locationId?: number) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);

  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  const loadAppointments = useCallback(async () => {
    if (!patientId || !locationId) return;

    try {
      setIsLoading(true);
      const appts = await appointmentService.getPatientAppointments(patientId, locationId);
      setAppointments(appts.sort((a, b) => moment(b.date).diff(moment(a.date))));
    } catch (e: any) {
      if (e?.status !== 404) showError('Unable to load appointments');
    } finally {
      setIsLoading(false);
    }
  }, [appointmentService, patientId, locationId]);

  const getMoreData = useCallback(
    async (appointment: AppointmentData) => {
      if (!locationId) return;

      if (!appointment.moreData) {
        try {
          const moreData = await appointmentService.getAppointmentById(appointment.id, locationId);
          setAppointments(apt => apt.map(a => (a.id === appointment.id ? { ...a, moreData } : a)));
        } catch (e: any) {
          if (e?.status !== 404) showError('Unable to load appointment details');
        }
      }
    },
    [appointmentService, locationId]
  );

  const updateAppointmentStatus = useCallback(
    async (appointment: AppointmentData, status: Appointment['status']) => {
      if (!locationId) return;

      try {
        setAppointments(apt => apt.map(a => (a.id === appointment.id ? { ...a, status } : a)));
        await appointmentService.updateAppointmentStatus(appointment.id, locationId, status);
      } catch {
        setAppointments(apt => apt.map(a => (a.id === appointment.id ? { ...a, status: appointment.status } : a)));
        showError('Unable to update appointment status');
      }
    },
    [appointmentService, locationId]
  );

  useEffect(() => {
    loadAppointments();
  }, [patientId, locationId]);

  return { appointments, isLoading, loadAppointments, getMoreData, updateAppointmentStatus };
};
